// ============================================
// PRIVACY POLICY PAGE
// ============================================

import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, MessageCircle, Lock, Eye } from 'lucide-react';
import { Layout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle, Button } from '../../components/ui';
import { APP_NAME, COMPANY_FULL_NAME, SERVICE_FEE, EXPERT_YEARS, ROUTES } from '../../constants';
import styles from './Legal.module.css';

import { useEffect } from 'react';
// ...existing imports...

export function PrivacyPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);
  return (
    <Layout maxWidth="md">
      <div className={styles.container}>
        <Link to={ROUTES.HOME} className={styles.backLink}>
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        <Card variant="elevated">
          <CardHeader>
            <div className={styles.headerIcon}>
              <Shield size={32} />
            </div>
            <CardTitle>Privacy Policy</CardTitle>
            <p className={styles.lastUpdated}>Last updated: {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
          </CardHeader>

          <CardContent>
            <div className={styles.content}>
              
              <section className={styles.section}>
                <h2>1. Who We Are</h2>
                <p>
                  <strong>{COMPANY_FULL_NAME}</strong> ("{APP_NAME}") is a device recommendation service 
                  with <strong>{EXPERT_YEARS}+ years of industry experience</strong>. We help customers 
                  make informed decisions when purchasing laptops and desktops.
                </p>
              </section>

              <section className={styles.highlightSection}>
                <div className={styles.highlightHeader}>
                  <MessageCircle size={20} />
                  <h3>Why We Collect Your Information</h3>
                </div>
                <div className={styles.whatsappBox}>
                  <p>
                    We collect your contact information, specifically your <strong>WhatsApp number</strong>, 
                    to provide you with personalized device recommendations.
                  </p>
                  <p className={styles.emphasized}>
                    This is exactly why we charge <strong>9{SERVICE_FEE}</strong>  you get direct access to 
                    our experts with {EXPERT_YEARS}+ years of experience who will personally guide you 
                    through WhatsApp.
                  </p>
                </div>
              </section>

              <section className={styles.section}>
                <h2>2. Information We Collect</h2>
                <div className={styles.infoGrid}>
                  <div className={styles.infoCard}>
                    <Eye size={20} />
                    <h4>Personal Information</h4>
                    <ul>
                      <li>Name</li>
                      <li>Email address</li>
                      <li>Phone / WhatsApp number</li>
                      <li>City / Location</li>
                    </ul>
                  </div>
                  <div className={styles.infoCard}>
                    <Lock size={20} />
                    <h4>Usage Information</h4>
                    <ul>
                      <li>Device preferences</li>
                      <li>Budget range</li>
                      <li>Current device details</li>
                      <li>Technical requirements</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h2>3. How We Use Your Information</h2>
                <table className={styles.purposeTable}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>WhatsApp Number</td>
                      <td>To send you personalized recommendations via WhatsApp</td>
                    </tr>
                    <tr>
                      <td>Email Address</td>
                      <td>For authentication, receipts, and backup communication</td>
                    </tr>
                    <tr>
                      <td>Phone Number</td>
                      <td>For order confirmation and urgent communications</td>
                    </tr>
                    <tr>
                      <td>Form Responses</td>
                      <td>To understand your needs and provide accurate recommendations</td>
                    </tr>
                    <tr>
                      <td>Location</td>
                      <td>To suggest locally available products and services</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className={styles.section}>
                <h2>4. WhatsApp Communication</h2>
                <div className={styles.importantBox}>
                  <p>
                    By providing your WhatsApp number and completing payment, you consent to receive:
                  </p>
                  <ul>
                    <li>Your personalized device recommendation</li>
                    <li>Follow-up questions for clarification (if needed)</li>
                    <li>Alternative suggestions based on availability</li>
                  </ul>
                  <p>
                    <strong>We do not spam.</strong> Communications are limited to your recommendation 
                    and direct responses to your queries.
                  </p>
                </div>
              </section>

              <section className={styles.section}>
                <h2>5. Data Protection</h2>
                <p>We implement appropriate security measures to protect your data:</p>
                <ul>
                  <li>Secure HTTPS encryption for all data transmission</li>
                  <li>Payment processing through Razorpay (PCI-DSS compliant)</li>
                  <li>Access controls to limit who can view your information</li>
                  <li>Regular security reviews of our systems</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>6. Data Sharing</h2>
                <p>
                  <strong>We do not sell your personal information.</strong> Your data may only be 
                  shared with:
                </p>
                <ul>
                  <li><strong>Razorpay</strong> – for payment processing</li>
                  <li><strong>Supabase</strong> – for secure data storage</li>
                  <li><strong>Legal authorities</strong> – if required by law</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>7. Data Retention</h2>
                <p>
                  We retain your information for the duration necessary to provide our service and 
                  for legal/accounting purposes. You may request deletion of your data by contacting us.
                </p>
              </section>

              <section className={styles.section}>
                <h2>8. Your Rights</h2>
                <p>You have the right to:</p>
                <ul>
                  <li>Access the personal data we hold about you</li>
                  <li>Request correction of inaccurate data</li>
                  <li>Request deletion of your data (subject to legal requirements)</li>
                  <li>Withdraw consent for future communications</li>
                </ul>
              </section>

              <section className={styles.section}>
                <h2>9. Contact Us</h2>
                <p>
                  For privacy-related inquiries, please contact us via our website or WhatsApp.
                </p>
              </section>

            </div>
          </CardContent>
        </Card>

        <div className={styles.footer}>
          <Button variant="outline" onClick={() => window.history.back()}>
            <ArrowLeft size={18} />
            Go Back
          </Button>
        </div>
      </div>
    </Layout>
  );
}
